import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/utils";
export type BadgeVariant = VariantProps<typeof badgeVariants>["variant"];
const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-full border px-2 py-1 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default: "border-transparent bg-blue-50 text-blue-500 [a&]:hover:bg-primary/90",
        secondary: "border-transparent bg-gray-50 text-gray-400 [a&]:hover:bg-secondary/90",
        destructive:
          "border-transparent bg-red-50 text-red-500 [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "text-blue-500 border-blue-500 [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        success:
          "border-transparent bg-green-50 text-green-500 [a&]:hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400",
        warning:
          "border-transparent bg-orange-50 text-orange-500 [a&]:hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400",
        info: "border-transparent bg-gray-50 text-gray-500 [a&]:hover:bg-gray-200",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "span";

  return (
    <Comp data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
